import { useEffect, useState } from 'react';
import { FiEdit2, FiX } from 'react-icons/fi';
import { BiBuildings, BiMehBlank } from 'react-icons/bi';
import { LuCircleCheck } from 'react-icons/lu';
import { FaUser } from 'react-icons/fa';
import { useAuth } from '../../providers/auth.provider';
import toast from 'react-hot-toast';
import authService from '../../services/api/auth.service';
import bankDetailsService from '../../services/api/bankdetail.service';
import AddPayoutDetailsCanvas from '../../components/offcanvas/add-payout';
import VeriFyEmailOTP from '../../components/modals/verifyemail';
import validator from 'validator';

// Types
interface InputFieldProps {
    label: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    type?: string;
    required?: boolean;
    error?: string;
    rightIcon?: React.ReactNode;
    prefix?: React.ReactNode;
}

interface BankDetails {
    bank_name: string;
    account_holder_name: string;
    account_number: string;
    ifsc_code: string;
}

interface PayoutState {
    bankName: string;
    accountHolderName: string;
    accountNumber: string;
    ifscCode: string;
}

// Common Components
const EditButtons: React.FC<{ isEditing: boolean; setIsEditing: (value: boolean) => void; onSave: () => void; editText?: string; }> = ({ isEditing, setIsEditing, onSave, editText = 'Edit details' }) => (
    <div className="flex items-center justify-between mb-6">
        <button
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center gap-2 text-primary-800 px-4 py-2 rounded-md border border-primary-800"
        >
            {isEditing ? (
                <>
                    <FiX className="w-4 h-4" />
                    Close
                </>
            ) : (
                <>
                    <FiEdit2 className="w-4 h-4" />
                    {editText}
                </>
            )}
        </button>
        {isEditing && (
            <button
                onClick={onSave}
                className="px-4 py-2 bg-primary-800 text-white rounded-md hover:bg-primary-700"
            >
                Save Changes
            </button>
        )}
    </div>
);

const InputField: React.FC<InputFieldProps> = ({ label, value, onChange, disabled = false, type = 'text', required = false, error, rightIcon, prefix }) => (
    <div>
        <label className="block text-sm text-gray-600 mb-1">
            {label} {required && '*'}
        </label>
        <div className="relative">
            {prefix && (
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500">
                    {prefix}
                </span>
            )}
            <input
                type={type}
                value={value}
                onChange={onChange}
                disabled={disabled}
                className={`w-full p-2 border rounded-md ${disabled ? 'bg-gray-50 hover:cursor-not-allowed' : ''
                    } ${prefix ? 'pl-12' : ''} ${rightIcon ? 'pr-10' : ''}`}
            />
            {rightIcon && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2">
                    {rightIcon}
                </span>
            )}
        </div>
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
);

interface ProfileState {
    first_name: string;
    last_name: string;
    email: string;
    phone_no: string;
    pan: string;
}

interface ValidationErrors {
    first_name?: string;
    last_name?: string;
    email?: string;
    pan?: string;
}

const ProfileDetails: React.FC = () => {
    const { userData, fetchUserData } = useAuth();
    const [isEditing, setIsEditing] = useState(false);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [details, setDetails] = useState<ProfileState>({
        first_name: '',
        last_name: '',
        email: '',
        phone_no: '',
        pan: '',

    });

    // Load user data into the form
    useEffect(() => {
        if (userData) {
            setDetails({
                first_name: userData.first_name || '',
                last_name: userData.last_name || '',
                email: userData.email || '',
                phone_no: userData.phone_no || '',
                pan: userData.customer?.pan || '',
            });
        }
    }, [userData]);

    // Validate individual fields
    const validateField = (name: string, value: string): string | undefined => {
        switch (name) {
            // case 'first_name':
            //     if (validator.isEmpty(value.trim())) {
            //         return 'First Name is required';
            //     }
            //     if (!validator.isAlpha(value.replace(/\s/g, ''))) {
            //         return 'Only letters are allowed in First Name';
            //     }
            //     break;
            // case 'last_name':
            //     if (validator.isEmpty(value.trim())) {
            //         return 'Last Name is required';
            //     }
            //     if (!validator.isAlpha(value.replace(/\s/g, ''))) {
            //         return 'Only letters are allowed in Last Name';
            //     }
            //     break;
            // case 'email':
            //     if (!validator.isEmail(value)) {
            //         return 'Please enter a valid email address';
            //     }
            //     break;
            case 'pan':
                if (!validator.matches(value, /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/)) {
                    return 'Invalid PAN format (e.g., ABCDE1234F)';
                }
                break;
        }
    };

    // Handle field changes
    const handleFieldChange = (field: keyof ProfileState, value: string) => {
        setDetails(prev => ({ ...prev, [field]: value }));

        const error = validateField(field, value);
        setErrors(prev => ({
            ...prev,
            [field]: error,
        }));
    };

    // Validate the entire form
    const validateForm = (): boolean => {
        const newErrors: ValidationErrors = {};
        let isValid = true;

        Object.keys(details).forEach((key) => {
            if (key !== 'phone_no') { // Skip validation for non-editable phone_no field
                const error = validateField(key, details[key as keyof ProfileState]);
                if (error) {
                    newErrors[key as keyof ValidationErrors] = error;
                    isValid = false;
                }
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    // Save profile details
    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            const response = await authService.updatePan({
                pan: details.pan,
            });
            toast.success(response.message);
            setIsEditing(false);
            fetchUserData();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Something went wrong');
        }
    };

    // Verify email via OTP
    const handleVerifyOTP = async () => {
        try {
            const response = await authService.sendEmailOTP({ email: details.email });
            toast.success(response.message);
            setEmailModalOpen(true);
            setIsEditing(false);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Something went wrong');
        }
    };

    return (
        <>
            <div className="space-y-4 max-w-2xl">
                {/* {(userData?.customer.application_status === null || userData?.customer.application_status === '') && <EditButtons isEditing={isEditing} setIsEditing={setIsEditing} editText='Change PAN' onSave={handleSave} />} */}

                <div className="space-y-4">
                    <InputField
                        label="First Name"
                        value={details.first_name}
                        onChange={(e) => handleFieldChange('first_name', e.target.value)}
                        disabled={true}
                        required
                        error={errors.first_name}
                    />
                    <InputField
                        label="Last Name"
                        value={details.last_name}
                        onChange={(e) => handleFieldChange('last_name', e.target.value)}
                        disabled={true}
                        required
                        error={errors.last_name}
                    />
                    <InputField
                        label="Email"
                        value={details.email}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        disabled={true}
                        required
                        error={errors.email}
                        rightIcon={
                            userData?.is_email_verified ? (
                                <LuCircleCheck className="w-4 h-4 text-green-500" />
                            ) : (
                                <FiX className="w-4 h-4 text-red-500" />
                            )
                        }
                    />
                    {/* {!userData?.is_email_verified && (
                        <button
                            type="button"
                            onClick={handleVerifyOTP}
                            className="text-primary-800 font-bold text-md"
                        >
                            Verify Email?
                        </button>
                    )} */}
                    <InputField
                        label="Phone Number"
                        value={details.phone_no}
                        onChange={() => { }}
                        disabled={true}
                        required
                        prefix="+91"
                        rightIcon={
                            userData?.is_phone_verified ? (
                                <LuCircleCheck className="w-4 h-4 text-green-500" />
                            ) : (
                                <FiX className="w-4 h-4 text-red-500" />
                            )
                        }
                    />
                    <InputField
                        label="PAN"
                        value={details.pan}
                        onChange={(e) => handleFieldChange('pan', e.target.value)}
                        disabled={true}
                        required
                        error={errors.pan}
                    />
                </div>
            </div>
            <VeriFyEmailOTP
                emailModalOpen={emailModalOpen}
                setEmailModalOpen={setEmailModalOpen}
                email={details.email}
            />
        </>
    );
};

// Payout Details Component
const PayoutDetails: React.FC = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [showPayoutCanvas, setShowPayoutCanvas] = useState(false);
    const { userData, fetchUserData } = useAuth();
    const [bankData, setBankData] = useState<BankDetails | null>(null);
    const [details, setDetails] = useState<PayoutState>({
        bankName: '',
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
    });
    const [errors, setErrors] = useState<Partial<PayoutState>>({});

    const validators = {
        gst: (value: string) => {
            if (!value) return '';
            const regex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
            return regex.test(value) ? '' : 'Invalid GST format. Example: 27AAPFU0939F1Z5';
        },
        pan: (value: string) => {
            const regex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
            return regex.test(value) ? '' : 'Invalid PAN format. Example: ABCDE1234A';
        }
    };

    const validateFields = () => {
        const newErrors: Partial<PayoutState> = {};
        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error);
    };

    const fetchPayoutDetails = async () => {
        try {
            const response = await bankDetailsService.getBankDetails();
            if (response.success && response.data.bankDetails) {
                setBankData(response.data.bankDetails);
                setDetails({
                    bankName: response.data.bankDetails.bank_name || '',
                    accountHolderName: response.data.bankDetails.account_holder_name || '',
                    accountNumber: response.data.bankDetails.account_number || '',
                    ifscCode: response.data.bankDetails.ifsc_code || '',
                });
            }
        } catch (error: any) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchPayoutDetails();
    }, [userData]);

    const handleSave = async () => {
        if (validateFields()) {
            try {
                const response = await bankDetailsService.updateBankDetails({
                    bank_name: details.bankName,
                    account_holder_name: details.accountHolderName,
                    account_number: details.accountNumber,
                    ifsc_code: details.ifscCode,
                });
                toast.success(response.message);
                setIsEditing(false);
                fetchPayoutDetails();
                fetchUserData();
            } catch (error: any) {
                toast.error(error.response?.data?.message || 'Something went wrong');
            }
        }
    };

    if (!bankData) {
        return (
            <>
                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <h2 className="text-xl font-semibold text-gray-900">Payout details not added</h2>
                    <p className="text-gray-600 text-center max-w-md">
                        Add your payout details for hassle-free earnings
                    </p>
                    <button
                        onClick={() => setShowPayoutCanvas(true)}
                        className="w-full max-w-md py-3 bg-primary-800 text-white rounded-lg hover:bg-primary-900 transition-colors"
                    >
                        Add payout details
                    </button>
                </div>
                <AddPayoutDetailsCanvas
                    fetchPayoutDetails={fetchPayoutDetails}
                    showPayoutCanvas={showPayoutCanvas}
                    setShowPayoutCanvas={setShowPayoutCanvas}
                />
            </>
        );
    }

    return (
        <>
            <div className="max-w-4xl space-y-6">
                <EditButtons isEditing={isEditing} setIsEditing={setIsEditing} onSave={handleSave} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Bank Details Section */}
                    <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <BiMehBlank className="text-primary-600" />
                                Bank Details
                            </h3>
                            <LuCircleCheck className="text-green-500 text-xl" />
                        </div>

                        <div className="space-y-4">
                            <InputField
                                label="Bank Name"
                                value={details.bankName}
                                onChange={(e) => setDetails(prev => ({ ...prev, bankName: e.target.value }))}
                                disabled={!isEditing}
                                required
                                error={errors.bankName}
                            />
                            <InputField
                                label="Account Holder"
                                value={details.accountHolderName}
                                onChange={(e) => setDetails(prev => ({ ...prev, accountHolderName: e.target.value }))}
                                disabled={!isEditing}
                                required
                                error={errors.accountHolderName}
                            />
                            <InputField
                                label="Account Number"
                                value={details.accountNumber}
                                onChange={(e) => setDetails(prev => ({ ...prev, accountNumber: e.target.value }))}
                                disabled={!isEditing}
                                required
                                error={errors.accountNumber}
                            />
                            <InputField
                                label="IFSC Code"
                                value={details.ifscCode}
                                onChange={(e) => setDetails(prev => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))}
                                disabled={!isEditing}
                                required
                                error={errors.ifscCode}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

// Main Account Details Component
const AccountDetails: React.FC = () => {
    const [activeTab, setActiveTab] = useState('profile');

    const tabs = [
        { id: 'profile', label: 'Profile details', icon: FaUser },
        // { id: 'payout', label: 'Payout details', icon: BiBuildings }
    ];

    return (
        <div className="w-full">
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h1 className="text-3xl font-bold mb-8 text-gray-800">Account Details</h1>

                <div className="border-b mb-6">
                    <div className="flex gap-4">
                        {tabs.map(({ id, label, icon: Icon }) => (
                            <button
                                key={id}
                                onClick={() => setActiveTab(id)}
                                className={`flex items-center gap-2 px-4 py-2 -mb-px transition-colors duration-200 ${activeTab === id
                                    ? 'text-primary-800 border-b-2 border-primary-800'
                                    : 'text-gray-600 hover:text-gray-800'
                                    }`}
                            >
                                <Icon className="w-4 h-4" />
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {activeTab === 'profile' && <ProfileDetails />}
                {activeTab === 'payout' && <PayoutDetails />}
            </div>
        </div>
    );
};

export default AccountDetails;